import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  FEATURED_PLAN,
  FEATURED_IDEA_ID,
  FEATURED_IDEA_TITLE,
  FEATURED_LOCATION,
} from "@/lib/featured";

const FeapAnchorBand = () => {
  return (
    <section id="anchor-case-study" className="bg-cream py-12 md:py-16 scroll-mt-24">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-[var(--radius)] bg-card border border-border shadow-card p-6 md:p-9"
        >
          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide uppercase bg-primary/10 text-primary">
              <Landmark className="h-3 w-3" />
              Our anchoring case study
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold tracking-wide uppercase bg-muted text-muted-foreground">
              <MapPin className="h-3 w-3" />
              {FEATURED_PLAN.location}
            </span>
          </div>

          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground leading-tight mb-4 max-w-3xl">
            Executing the{" "}
            <span className="text-primary">{FEATURED_PLAN.shortTitle}</span> — one
            initiative at a time.
          </h2>

          <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl mb-6">
            A city-adopted strategy to close Mobile&apos;s wealth gap — banking access,
            DBE ownership, and priority-zip revitalization. Our first initiative under
            it:{" "}
            <Link
              to={`/ideas/${FEATURED_IDEA_ID}`}
              className="text-foreground underline underline-offset-4 decoration-primary/60 hover:decoration-primary"
            >
              {FEATURED_IDEA_TITLE.replace("Revitalize ", "revitalizing ")}
            </Link>{" "}
            in {FEATURED_LOCATION}.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-7">
            {FEATURED_PLAN.stats.map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-border bg-muted/40 px-4 py-3"
              >
                <div className="font-display text-2xl md:text-3xl font-bold text-primary">
                  {s.value}
                </div>
                <div className="text-[11px] md:text-xs text-muted-foreground mt-1 leading-snug">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="rounded-full font-display font-semibold">
              <Link to={FEATURED_PLAN.href}>
                Read the plan analysis <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full font-display font-semibold"
            >
              <a href="#initiatives">See the initiatives</a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeapAnchorBand;